import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle, XCircle, Clock, FileText, Mail, Loader2,
  ArrowLeft, Play, Eye, StopCircle, Download, Plus, Edit, Trash2,
  Globe, GlobeLock, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/RichTextEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Article Editor Modal
function ArticleEditor({ article, adminKey, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: article?.title || '',
    body: article?.body || '',
    excerpt: article?.excerpt || '',
    meta_description: article?.meta_description || '',
    seo_tags: article?.seo_tags?.join(', ') || '',
    featured_image_url: article?.featured_image_url || '',
    category: article?.category || '',
    author: article?.author || 'ContractWiser',
    published: article?.published || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        seo_tags: form.seo_tags.split(',').map(t => t.trim()).filter(Boolean),
      };
      if (article?.slug) {
        await axios.put(`${API}/admin/articles/${article.slug}?admin_key=${adminKey}`, payload);
        toast.success("Article updated!");
      } else {
        await axios.post(`${API}/admin/articles?admin_key=${adminKey}`, payload);
        toast.success("Article created!");
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="article-editor">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">{article ? 'Edit Article' : 'New Article'}</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {article ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Title *</label>
            <Input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Article title"
              className="mt-1"
              data-testid="article-title-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Body</label>
            <div className="mt-1">
              <RichTextEditor
                content={form.body}
                onChange={body => setForm(f => ({ ...f, body }))}
                placeholder="Write your article content here..."
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Excerpt</label>
            <textarea
              value={form.excerpt}
              onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
              placeholder="Brief summary shown in article cards..."
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              data-testid="article-excerpt-input"
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Publish</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="article-publish-toggle">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{form.published ? 'Published' : 'Draft'}</span>
              </label>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">SEO Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Meta Description</label>
                <textarea
                  value={form.meta_description}
                  onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
                  placeholder="SEO description (150-160 chars)"
                  rows={3}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm mt-1"
                  data-testid="article-meta-input"
                />
                <span className="text-xs text-gray-400">{form.meta_description.length}/160</span>
              </div>
              <div>
                <label className="text-xs text-gray-500">SEO Tags (comma separated)</label>
                <Input
                  value={form.seo_tags}
                  onChange={e => setForm(f => ({ ...f, seo_tags: e.target.value }))}
                  placeholder="property, contract, NSW"
                  className="mt-1"
                  data-testid="article-tags-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Featured Image URL</label>
                <Input
                  value={form.featured_image_url}
                  onChange={e => setForm(f => ({ ...f, featured_image_url: e.target.value }))}
                  placeholder="https://..."
                  className="mt-1"
                  data-testid="article-image-input"
                />
                {form.featured_image_url && (
                  <img src={form.featured_image_url} alt="Preview" className="mt-2 rounded-md w-full h-32 object-cover" />
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Category</label>
                <Input
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  placeholder="e.g., Buying Guide"
                  className="mt-1"
                  data-testid="article-category-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Author</label>
                <Input
                  value={form.author}
                  onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                  placeholder="Author name"
                  className="mt-1"
                  data-testid="article-author-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [adminKey, setAdminKey] = useState(searchParams.get('key') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('submissions');
  
  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  
  // Articles state
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/submissions?admin_key=${adminKey}&limit=50`);
      setSubmissions(response.data.submissions);
      setAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Invalid admin key");
        setAuthenticated(false);
      } else {
        toast.error("Failed to fetch submissions");
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  const fetchArticles = useCallback(async () => {
    if (!adminKey) return;
    setArticlesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/articles?admin_key=${adminKey}`);
      setArticles(response.data.articles);
    } catch (err) {
      toast.error("Failed to fetch articles");
    } finally {
      setArticlesLoading(false);
    }
  }, [adminKey]);

  const triggerAnalysis = async (submissionId) => {
    setProcessingId(submissionId);
    try {
      await axios.post(`${API}/admin/process/${submissionId}?admin_key=${adminKey}`);
      toast.success("Analysis started! It may take 1-3 minutes.");
      setTimeout(fetchSubmissions, 5000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to trigger analysis");
    } finally {
      setProcessingId(null);
    }
  };

  const cancelJob = async (submissionId) => {
    setCancellingId(submissionId);
    try {
      await axios.post(`${API}/admin/cancel/${submissionId}?admin_key=${adminKey}`);
      toast.success("Job cancelled");
      fetchSubmissions();
    } catch (err) {
      toast.error("Failed to cancel");
    } finally {
      setCancellingId(null);
    }
  };

  const downloadContract = async (submissionId, filename) => {
    try {
      const response = await axios.get(`${API}/admin/download/${submissionId}?admin_key=${adminKey}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'contract.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download contract");
    }
  };

  const deleteArticle = async (slug) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/articles/${slug}?admin_key=${adminKey}`);
      toast.success("Article deleted");
      fetchArticles();
    } catch (err) {
      toast.error("Failed to delete article");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { bg: 'bg-green-100 text-green-700', icon: <CheckCircle className="h-3 w-3" />, label: 'Completed' },
      failed: { bg: 'bg-red-100 text-red-700', icon: <XCircle className="h-3 w-3" />, label: 'Failed' },
      cancelled: { bg: 'bg-gray-100 text-gray-700', icon: <StopCircle className="h-3 w-3" />, label: 'Cancelled' },
      analyzing: { bg: 'bg-blue-100 text-blue-700', icon: <Loader2 className="h-3 w-3 animate-spin" />, label: 'Analyzing' },
      paid: { bg: 'bg-amber-100 text-amber-700', icon: <Clock className="h-3 w-3" />, label: 'Paid' },
    };
    const b = badges[status] || { bg: 'bg-gray-100 text-gray-700', icon: <Clock className="h-3 w-3" />, label: status };
    return <span className={`inline-flex items-center gap-1 px-2 py-1 ${b.bg} rounded-full text-xs font-medium`}>{b.icon} {b.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Admin Key</label>
                <Input
                  type="password"
                  value={adminKey}
                  onChange={(e) => setAdminKey(e.target.value)}
                  placeholder="Enter admin key"
                  className="mt-1"
                  data-testid="admin-key-input"
                  onKeyDown={(e) => e.key === 'Enter' && fetchSubmissions()}
                />
              </div>
              <Button onClick={fetchSubmissions} className="w-full" disabled={!adminKey || loading} data-testid="admin-login-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-2" />Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <Button onClick={activeTab === 'submissions' ? fetchSubmissions : fetchArticles} disabled={loading || articlesLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${(loading || articlesLoading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg p-1 border border-gray-200 w-fit" data-testid="admin-tabs">
          <button
            onClick={() => { setActiveTab('submissions'); if (submissions.length === 0) fetchSubmissions(); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'submissions' ? 'bg-[#2E4033] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="admin-tab-submissions"
          >
            <FileText className="h-4 w-4 inline mr-1.5" />Submissions
          </button>
          <button
            onClick={() => { setActiveTab('articles'); fetchArticles(); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'articles' ? 'bg-[#2E4033] text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="admin-tab-articles"
          >
            <Edit className="h-4 w-4 inline mr-1.5" />Articles
          </button>
        </div>

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { label: 'Total', count: submissions.length, color: 'text-gray-600' },
                { label: 'Completed', count: submissions.filter(s => s.status === 'completed').length, color: 'text-green-600' },
                { label: 'Failed', count: submissions.filter(s => s.status === 'failed').length, color: 'text-red-600' },
                { label: 'Analyzing', count: submissions.filter(s => s.status === 'analyzing').length, color: 'text-blue-600' },
                { label: 'Pending', count: submissions.filter(s => s.status === 'pending' || s.status === 'paid').length, color: 'text-amber-600' },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-4 text-center">
                    <div className={`text-2xl font-bold ${s.color}`}>{s.count}</div>
                    <div className="text-sm text-gray-500">{s.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Table */}
            <Card>
              <CardHeader><CardTitle>Submissions</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Email</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">File</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Size</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Status</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Email Sent</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Created</th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((sub) => (
                        <tr key={sub.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-gray-400" />
                              <span className="truncate max-w-[200px]">{sub.email}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="truncate max-w-[200px]" title={sub.original_filename}>{sub.original_filename || '-'}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">{formatFileSize(sub.file_size)}</td>
                          <td className="py-3 px-2">{getStatusBadge(sub.status)}</td>
                          <td className="py-3 px-2">
                            {sub.email_sent ? <CheckCircle className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-gray-300" />}
                          </td>
                          <td className="py-3 px-2 text-gray-500">{formatDate(sub.created_at)}</td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              {sub.storage_path && (
                                <Button size="sm" variant="outline" onClick={() => downloadContract(sub.id, sub.original_filename)} title="Download">
                                  <Download className="h-3 w-3" />
                                </Button>
                              )}
                              {sub.status === 'completed' ? (
                                <Button size="sm" variant="outline" onClick={() => navigate(`/report?submission_id=${sub.id}`)}>
                                  <Eye className="h-3 w-3 mr-1" />View
                                </Button>
                              ) : sub.status === 'analyzing' ? (
                                <Button size="sm" variant="destructive" onClick={() => cancelJob(sub.id)} disabled={cancellingId === sub.id}>
                                  {cancellingId === sub.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <StopCircle className="h-3 w-3 mr-1" />}
                                  Stop
                                </Button>
                              ) : sub.status !== 'completed' ? (
                                <Button size="sm" onClick={() => triggerAnalysis(sub.id)} disabled={processingId === sub.id}>
                                  {processingId === sub.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
                                  {sub.status === 'failed' || sub.status === 'cancelled' ? 'Retry' : 'Start'}
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {submissions.length === 0 && <div className="text-center py-8 text-gray-500">No submissions found</div>}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Articles Tab */}
        {activeTab === 'articles' && (
          <>
            {showEditor ? (
              <ArticleEditor
                article={editingArticle}
                adminKey={adminKey}
                onSave={() => { setShowEditor(false); setEditingArticle(null); fetchArticles(); }}
                onCancel={() => { setShowEditor(false); setEditingArticle(null); }}
              />
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-500">{articles.length} article{articles.length !== 1 ? 's' : ''}</p>
                  <Button onClick={() => { setEditingArticle(null); setShowEditor(true); }} data-testid="new-article-btn">
                    <Plus className="h-4 w-4 mr-2" />New Article
                  </Button>
                </div>

                <div className="space-y-3" data-testid="admin-articles-list">
                  {articlesLoading ? (
                    <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></div>
                  ) : articles.length === 0 ? (
                    <Card>
                      <CardContent className="text-center py-12 text-gray-500">
                        No articles yet. Click "New Article" to create one.
                      </CardContent>
                    </Card>
                  ) : (
                    articles.map(article => (
                      <Card key={article.id} className="hover:shadow-sm transition-shadow">
                        <CardContent className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-4 min-w-0 flex-1">
                            {article.featured_image_url && (
                              <img src={article.featured_image_url} alt="" className="w-16 h-12 object-cover rounded flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-gray-900 truncate">{article.title}</h3>
                                {article.published ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">
                                    <Globe className="h-3 w-3" />Published
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs">
                                    <GlobeLock className="h-3 w-3" />Draft
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-400">
                                {article.category && <span>{article.category}</span>}
                                <span>{article.author}</span>
                                <span>{formatDate(article.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                            {article.published && (
                              <Button size="sm" variant="ghost" asChild>
                                <a href={`/articles/${article.slug}`} target="_blank" rel="noreferrer" title="View">
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm" variant="outline"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/admin/articles/${article.slug}?admin_key=${adminKey}`);
                                  setEditingArticle(res.data);
                                  setShowEditor(true);
                                } catch {
                                  setEditingArticle(article);
                                  setShowEditor(true);
                                }
                              }}
                              data-testid={`edit-article-${article.slug}`}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => deleteArticle(article.slug)} data-testid={`delete-article-${article.slug}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
